'use client'

import { useQuery } from '@tanstack/react-query'
import { dashboardConfigApi } from '@/lib/api'
import { BaseWidget, StatWidget } from './BaseWidget'
import { formatCurrency, formatNumber } from '@/lib/utils'
import type {
  CashCollectedData,
  ProfitData,
  RunwayData,
  ActiveRevenueClientsData,
  ProjectsAtRiskData,
  UtilizationRateData,
  TimeLoggedWeekData,
  UpcomingInvoicesData,
} from '@/types'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import {
  Banknote,
  TrendingUp,
  TrendingDown,
  Gauge,
  Users,
  AlertTriangle,
  PieChart,
  Clock,
  FileText,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
} from 'lucide-react'

// Cash Collected Widget
export function CashCollectedWidget() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['widget', 'CASH_COLLECTED'],
    queryFn: () => dashboardConfigApi.getWidgetData('CASH_COLLECTED'),
  })

  const cashData = data?.data as CashCollectedData | undefined

  if (isLoading || error || !cashData) {
    return (
      <StatWidget
        title="Cash Collected"
        value="--"
        subtitle="This month"
        icon={<Banknote className="h-4 w-4" />}
        isLoading={isLoading}
        error={error?.message}
      />
    )
  }

  const isPositive = cashData.percent_change >= 0
  const TrendIcon = isPositive ? TrendingUp : TrendingDown

  return (
    <StatWidget
      title="Cash Collected"
      value={formatCurrency(cashData.cash_collected, cashData.currency)}
      subtitle={`${cashData.invoices_paid_count} invoices paid`}
      icon={<Banknote className="h-4 w-4" />}
      trend={
        <span className={`flex items-center text-xs ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
          <TrendIcon className="h-3 w-3 mr-1" />
          {Math.abs(cashData.percent_change).toFixed(1)}% vs last month
        </span>
      }
    />
  )
}

// Profit Widget
export function ProfitWidget() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['widget', 'PROFIT'],
    queryFn: () => dashboardConfigApi.getWidgetData('PROFIT'),
  })

  const profitData = data?.data as ProfitData | undefined

  if (isLoading || error || !profitData) {
    return (
      <StatWidget
        title="Profit"
        value="--"
        subtitle="This month"
        icon={<TrendingUp className="h-4 w-4" />}
        isLoading={isLoading}
        error={error?.message}
      />
    )
  }

  return (
    <StatWidget
      title="Profit"
      value={formatCurrency(profitData.profit, profitData.currency)}
      subtitle="After expenses"
      icon={profitData.is_positive ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
      valueClassName={profitData.is_positive ? 'text-green-600' : 'text-red-600'}
    />
  )
}

// Runway Widget
export function RunwayWidget() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['widget', 'RUNWAY'],
    queryFn: () => dashboardConfigApi.getWidgetData('RUNWAY'),
  })

  const runwayData = data?.data as RunwayData | undefined

  if (isLoading || error || !runwayData) {
    return (
      <StatWidget
        title="Runway"
        value="--"
        subtitle="Months remaining"
        icon={<Gauge className="h-4 w-4" />}
        isLoading={isLoading}
        error={error?.message}
      />
    )
  }

  const runwayColor = runwayData.runway_months < 3 ? 'text-red-600' :
                      runwayData.runway_months < 6 ? 'text-yellow-600' : 'text-green-600'

  return (
    <StatWidget
      title="Runway"
      value={`${runwayData.runway_months.toFixed(1)} months`}
      subtitle={`Based on ${formatCurrency(runwayData.avg_monthly_expenses, runwayData.currency)}/mo expenses`}
      icon={<Gauge className="h-4 w-4" />}
      valueClassName={runwayColor}
    />
  )
}

// Active Revenue Clients Widget
export function ActiveRevenueClientsWidget() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['widget', 'ACTIVE_REVENUE_CLIENTS'],
    queryFn: () => dashboardConfigApi.getWidgetData('ACTIVE_REVENUE_CLIENTS'),
  })

  const clientsData = data?.data as ActiveRevenueClientsData | undefined

  if (isLoading || error || !clientsData) {
    return (
      <StatWidget
        title="Active Clients"
        value="--"
        subtitle="With active projects"
        icon={<Users className="h-4 w-4" />}
        isLoading={isLoading}
        error={error?.message}
      />
    )
  }

  const TrendIcon = clientsData.trend_direction === 'up' ? ArrowUpRight :
                    clientsData.trend_direction === 'down' ? ArrowDownRight : Minus
  const trendColor = clientsData.trend_direction === 'up' ? 'text-green-600' :
                     clientsData.trend_direction === 'down' ? 'text-red-600' : 'text-gray-500'

  return (
    <StatWidget
      title="Active Clients"
      value={formatNumber(clientsData.active_clients)}
      subtitle="With active projects"
      icon={<Users className="h-4 w-4" />}
      trend={
        <span className={`flex items-center text-xs ${trendColor}`}>
          <TrendIcon className="h-3 w-3 mr-1" />
          {Math.abs(clientsData.percent_change).toFixed(1)}% vs last month
        </span>
      }
    />
  )
}

// Projects At Risk Widget
export function ProjectsAtRiskWidget() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['widget', 'PROJECTS_AT_RISK'],
    queryFn: () => dashboardConfigApi.getWidgetData('PROJECTS_AT_RISK'),
  })

  const riskData = data?.data as ProjectsAtRiskData | undefined

  if (isLoading || error || !riskData) {
    return (
      <StatWidget
        title="Projects At Risk"
        value="--"
        subtitle="Needs attention"
        icon={<AlertTriangle className="h-4 w-4" />}
        isLoading={isLoading}
        error={error?.message}
      />
    )
  }

  const riskColor = riskData.total_at_risk === 0 ? 'text-green-600' :
                    riskData.total_at_risk <= 2 ? 'text-yellow-600' : 'text-red-600'

  return (
    <BaseWidget title="Projects At Risk" icon={<AlertTriangle className="h-4 w-4" />}>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className={`text-2xl font-bold ${riskColor}`}>{riskData.total_at_risk}</span>
          {riskData.total_at_risk > 0 && (
            <Link to="/projects" className="text-xs text-primary hover:underline">
              Review projects
            </Link>
          )}
        </div>
        {riskData.projects.slice(0, 3).map((project) => (
          <div key={project.project_id} className="flex items-center justify-between text-sm">
            <span className="truncate flex-1">{project.project_name}</span>
            <Badge variant={project.risk_reason === 'overdue_tasks' ? 'destructive' : 'secondary'} className="ml-2">
              {project.risk_reason === 'overdue_tasks' ? `${project.overdue_tasks} overdue` :
               project.risk_reason === 'no_activity' ? `${project.days_since_activity}d inactive` : 'Over budget'}
            </Badge>
          </div>
        ))}
        {riskData.total_at_risk === 0 && (
          <p className="text-sm text-muted-foreground">All projects on track!</p>
        )}
      </div>
    </BaseWidget>
  )
}

// Utilization Rate Widget
export function UtilizationRateWidget() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['widget', 'UTILIZATION_RATE'],
    queryFn: () => dashboardConfigApi.getWidgetData('UTILIZATION_RATE'),
  })

  const utilData = data?.data as UtilizationRateData | undefined

  if (isLoading || error || !utilData) {
    return (
      <StatWidget
        title="Utilization Rate"
        value="--"
        subtitle="Billable hours"
        icon={<PieChart className="h-4 w-4" />}
        isLoading={isLoading}
        error={error?.message}
      />
    )
  }

  const utilColor = utilData.utilization_rate < 50 ? 'text-red-600' :
                    utilData.utilization_rate < 70 ? 'text-yellow-600' : 'text-green-600'

  return (
    <BaseWidget title="Utilization Rate" icon={<PieChart className="h-4 w-4" />}>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className={`text-2xl font-bold ${utilColor}`}>{utilData.utilization_rate.toFixed(0)}%</span>
          <span className="text-xs text-muted-foreground">This {utilData.period}</span>
        </div>
        <Progress value={utilData.utilization_rate} className="h-2" />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{utilData.billable_hours.toFixed(1)}h billable</span>
          <span>{utilData.total_capacity_hours.toFixed(0)}h capacity</span>
        </div>
      </div>
    </BaseWidget>
  )
}

// Time Logged This Week Widget
export function TimeLoggedWeekWidget() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['widget', 'TIME_LOGGED_WEEK'],
    queryFn: () => dashboardConfigApi.getWidgetData('TIME_LOGGED_WEEK'),
  })

  const timeData = data?.data as TimeLoggedWeekData | undefined

  if (isLoading || error || !timeData) {
    return (
      <StatWidget
        title="Time Logged"
        value="--"
        subtitle="This week"
        icon={<Clock className="h-4 w-4" />}
        isLoading={isLoading}
        error={error?.message}
      />
    )
  }

  return (
    <BaseWidget title="Time Logged This Week" icon={<Clock className="h-4 w-4" />}>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-2xl font-bold">{timeData.total_hours.toFixed(1)}h</span>
          <Link to="/time-tracking" className="text-xs text-primary hover:underline">
            View timesheets
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <p className="text-muted-foreground">Avg per member</p>
            <p className="font-medium">{timeData.avg_per_member.toFixed(1)}h</p>
          </div>
          <div>
            <p className="text-muted-foreground">Team members</p>
            <p className="font-medium">{timeData.team_member_count}</p>
          </div>
        </div>
        <div className="text-xs text-muted-foreground">
          {timeData.billable_hours.toFixed(1)}h billable
        </div>
      </div>
    </BaseWidget>
  )
}

// Upcoming Invoices Widget
export function UpcomingInvoicesWidget() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['widget', 'UPCOMING_INVOICES'],
    queryFn: () => dashboardConfigApi.getWidgetData('UPCOMING_INVOICES'),
  })

  const invoiceData = data?.data as UpcomingInvoicesData | undefined

  if (isLoading || error || !invoiceData) {
    return (
      <StatWidget
        title="Upcoming Invoices"
        value="--"
        subtitle="This month"
        icon={<FileText className="h-4 w-4" />}
        isLoading={isLoading}
        error={error?.message}
      />
    )
  }

  return (
    <BaseWidget title="Upcoming Invoices" icon={<FileText className="h-4 w-4" />}>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-2xl font-bold">{formatCurrency(invoiceData.total_amount, invoiceData.currency)}</span>
          <span className="text-xs text-muted-foreground">{invoiceData.invoice_count} invoices</span>
        </div>
        {invoiceData.invoices.slice(0, 3).map((invoice) => (
          <div key={invoice.invoice_id} className="flex items-center justify-between text-sm">
            <div className="truncate flex-1">
              <span className="font-medium">{invoice.invoice_number}</span>
              <span className="text-muted-foreground ml-2">{invoice.client_name}</span>
            </div>
            <span className="ml-2">{formatCurrency(invoice.amount, invoice.currency)}</span>
          </div>
        ))}
        {invoiceData.recurring_count > 0 && (
          <p className="text-xs text-muted-foreground">
            + {invoiceData.recurring_count} recurring invoices
          </p>
        )}
      </div>
    </BaseWidget>
  )
}

